import React, { useState, useEffect, useRef, useMemo } from 'react';
import { X, BarChart3, Target, Loader2, Table, LineChart } from 'lucide-react';
import { useTheme } from '../../theme/ThemeContext';
import { BenchmarkService, StationMetrics, BenchmarkQuery } from '../../services/benchmarkService';
import { supabase } from '../auth/AuthProvider';
import { StationInfo, StationService } from '../../services/stationService';
import { benchmarkDatesService } from '../../services/benchmarkDatesService';
import { CustomDatePicker } from './CustomDatePicker';
import * as echarts from 'echarts';

interface BenchmarkModalProps {
  isOpen: boolean;
  onClose: () => void;
  stations: StationInfo[];
  selectedRegion: {
    north: number;
    south: number;
    east: number;
    west: number;
  } | null;
}

const BenchmarkModal: React.FC<BenchmarkModalProps> = ({
  isOpen,
  onClose,
  stations,
  selectedRegion,
}) => {
  const { theme } = useTheme();
  const [loading, setLoading] = useState(false);
  const [benchmarkData, setBenchmarkData] = useState<StationMetrics[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [selectedModels, setSelectedModels] = useState<Set<string>>(new Set(['ept2']));
  const [viewMode, setViewMode] = useState<'table' | 'chart'>('table');
  const [selectedVariable, setSelectedVariable] = useState<string>(
    'wind_speed_at_height_level_10m',
  );
  const [modelDropdownOpen, setModelDropdownOpen] = useState(false);
  const [latestObservationDate, setLatestObservationDate] = useState<string>('');
  const [availableDates, setAvailableDates] = useState<string[]>([]);
  const [loadingAvailableDates, setLoadingAvailableDates] = useState(false);
  const [maxForecastHours, setMaxForecastHours] = useState<number>(168); // Default 7 days (168 hours)
  const chartRef = useRef<HTMLDivElement>(null);
  const chartInstance = useRef<echarts.ECharts | null>(null);
  const modelDropdownRef = useRef<HTMLDivElement>(null);

  // Variable mapping with proper names and units
  const variableConfig: Record<string, { name: string; unit: string }> = {
    air_temperature_at_height_level_2m: { name: 'Air Temperature 2m [°C]', unit: '°C' },
    wind_speed_at_height_level_10m: { name: 'Wind Speed 10m [m/s]', unit: 'm/s' },
  };

  // Available models
  const MODELS = [
    { id: 'ept2', label: 'EPT-2' },
    { id: 'ept2_reasoning', label: 'EPT-2 Reasoning' },
    { id: 'ept2_hrrr', label: 'EPT-2 HRRR' },
    { id: 'ept2_early', label: 'EPT-2 Early' },
    { id: 'ept2_e', label: 'EPT-2e' },
    { id: 'ept1_5', label: 'EPT-1.5' },
    { id: 'ept1_5_early', label: 'EPT-1.5 Early' },
    { id: 'aurora', label: 'Microsoft Aurora' },
    { id: 'aifs', label: 'EC AIFS' },
    { id: 'ecmwf_ifs_single', label: 'EC IFS' },
    { id: 'noaa_gfs_single', label: 'NOAA GFS' },
  ];

  // Detect the temporal resolution of each model from the data
  const getModelResolution = (modelData: StationMetrics[]): number => {
    const timedeltas = modelData
      .map((m) => m.prediction_timedelta)
      .filter((t) => t > 0) // Exclude 0
      .sort((a, b) => a - b);

    if (timedeltas.length === 0) return 60; // Default to hourly

    // The smallest non-zero timedelta is the resolution
    return timedeltas[0];
  };

  // Calculate the lowest resolution among selected models
  const lowestResolution = useMemo(() => {
    if (benchmarkData.length === 0 || selectedModels.size === 0) {
      return 60; // Default to hourly
    }

    const resolutions: number[] = [];

    selectedModels.forEach((model) => {
      const modelData = benchmarkData.filter(
        (m: StationMetrics & { model?: string }) => m.model === model,
      );
      if (modelData.length > 0) {
        resolutions.push(getModelResolution(modelData));
      }
    });

    // Return the maximum resolution value (e.g., 360 for 6-hourly is "lower" than 60 for hourly)
    return resolutions.length > 0 ? Math.max(...resolutions) : 60;
  }, [benchmarkData, selectedModels]);

  // Filter benchmark data to only show values at the lowest resolution and within horizon
  const filteredBenchmarkData = useMemo(() => {
    const maxMinutes = maxForecastHours * 60; // Convert hours to minutes
    return benchmarkData.filter((metric: StationMetrics & { model?: string }) => {
      // Filter by forecast horizon
      if (metric.prediction_timedelta > maxMinutes) return false;

      // Always include prediction_timedelta = 0
      if (metric.prediction_timedelta === 0) return true;

      // Only include timedeltas that are multiples of the lowest resolution
      return metric.prediction_timedelta % lowestResolution === 0;
    });
  }, [benchmarkData, lowestResolution, maxForecastHours]);

  // Default to last 30 days
  const [endDate, setEndDate] = useState(() => {
    const today = new Date();
    return today.toISOString().split('T')[0]; // Format: YYYY-MM-DD
  });
  const [startDate, setStartDate] = useState(() => {
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    return thirtyDaysAgo.toISOString().split('T')[0]; // Format: YYYY-MM-DD
  });

  // Date validation functions
  const handleStartDateChange = (newStartDate: string) => {
    // Don't allow start date to be after end date or if not in available dates
    if (newStartDate > endDate) {
      return; // Don't update if it would create an invalid range
    }
    if (availableDates.length > 0 && !availableDates.includes(newStartDate)) {
      return; // Don't update if date is not available
    }
    setStartDate(newStartDate);
  };

  const handleEndDateChange = (newEndDate: string) => {
    // Only allow end date to be set if it's not before start date and is in available dates
    if (newEndDate >= startDate) {
      if (availableDates.length > 0 && !availableDates.includes(newEndDate)) {
        return; // Don't update if date is not available
      }
      setEndDate(newEndDate);
    }
  };

  useEffect(() => {
    if (isOpen && selectedRegion) {
      fetchBenchmarks();
    } else if (isOpen && !selectedRegion) {
      // Clear data when no region is selected
      setBenchmarkData([]);
      setError(null);
      setLoading(false);
    }
  }, [isOpen, selectedRegion, stations, selectedModels, startDate, endDate]);

  // Fetch initial data when modal opens
  useEffect(() => {
    if (isOpen && stations.length > 0) {
      fetchLatestObservationDate();

      // Fast initialization of dates using cache if available
      const initializeDatesFast = async () => {
        const firstModel = selectedModels.size > 0 ? Array.from(selectedModels)[0] : 'ept2'; // Default to ept2

        // Check cache status for debugging

        const dates = await benchmarkDatesService.getAvailableDates(firstModel);

        if (dates.length > 0) {
          // Set end date to most recent available benchmark date
          const latestDate = dates[0]; // dates are sorted desc

          // Set start date to 30 days before latest date, or earliest available date
          const thirtyDaysAgo = new Date(latestDate);
          thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
          const startDateCandidate = thirtyDaysAgo.toISOString().split('T')[0];

          // Find the best start date from available dates
          const availableStartDates = dates.filter((date) => date >= startDateCandidate);
          const bestStartDate =
            availableStartDates.length > 0
              ? availableStartDates[availableStartDates.length - 1] // Earliest within 30 days
              : dates[dates.length - 1]; // Or just the earliest available

          // Only update if dates are different from current (avoid unnecessary re-renders)
          if (endDate !== latestDate) {
            setEndDate(latestDate);
          }
          if (startDate !== bestStartDate) {
            setStartDate(bestStartDate);
          }

          setAvailableDates(dates);
        }
      };

      initializeDatesFast().catch(() => {
        // Fallback to normal fetch if fast init fails
        fetchAvailableDates();
      });
    }
  }, [isOpen, stations]);

  // Fetch available dates when selected models change (but not on initial load)
  useEffect(() => {
    if (isOpen && stations.length > 0) {
      fetchAvailableDates();
    }
  }, [selectedModels]);

  // Auto-refresh when variable changes
  useEffect(() => {
    if (isOpen && selectedVariable && benchmarkData.length > 0) {
      // Variable change doesn't require API call, just re-process existing data
      return;
    }
  }, [isOpen, selectedVariable, benchmarkData.length]);

  // Set default variable when data loads
  useEffect(() => {
    if (benchmarkData.length > 0 && !selectedVariable) {
      const firstMetric = benchmarkData[0];
      const availableVars = Object.keys(firstMetric.metrics);
      if (availableVars.length > 0) {
        setSelectedVariable(availableVars[0]);
      }
    }
  }, [benchmarkData, selectedVariable]);

  const fetchLatestObservationDate = async () => {
    if (!stations || stations.length === 0) return;

    try {
      const getToken = async () => {
        const { data } = await supabase.auth.getSession();
        const token = data.session?.access_token;
        if (!token) {
          throw new Error('No authentication token available');
        }
        return token;
      };

      // Use StationService to get the latest data point
      const stationService = new StationService(import.meta.env.VITE_API_BASE_URL, getToken);

      // Query a wide range to find the latest available data
      const endTime = new Date();
      const startTime = new Date();
      startTime.setDate(endTime.getDate() - 365); // Query last year to find latest data

      const response = await stationService.getStationData({
        station_ids: [stations[0].station], // Use first station as sample
        start_time: startTime.toISOString(),
        end_time: endTime.toISOString(),
        variables: ['air_temperature_at_height_level_2m'], // Use any variable
        limit: 1,
      });

      if (response.data && response.data.data.length > 0) {
        const latestDataPoint = response.data.data[0];
        const latestDate = new Date(latestDataPoint.time);
        const latestDateStr = latestDate.toISOString().split('T')[0];
        setLatestObservationDate(latestDateStr);

        // Also update the endDate if it's currently set to a date beyond the latest observation
        if (endDate > latestDateStr) {
          setEndDate(latestDateStr);
        }
      }
    } catch (err) {
      console.warn('Failed to fetch latest observation date:', err);
    }
  };

  const fetchAvailableDates = async () => {
    try {
      // Get available dates for the first selected model (or no model filter if none selected)
      const firstModel = selectedModels.size > 0 ? Array.from(selectedModels)[0] : undefined;

      // Check if we already have cached data - if so, don't show loading
      const cacheStatus = benchmarkDatesService.getCacheStatus();
      const cacheKey = firstModel || 'all_models';
      const hasCachedData = cacheStatus.some(
        (status) => status.model === cacheKey && status.dates > 0,
      );

      if (!hasCachedData) {
        setLoadingAvailableDates(true);
      }

      const dates = await benchmarkDatesService.getAvailableDates(firstModel);

      setAvailableDates(dates);

      // Update date selections to ensure they're within available dates
      if (dates.length > 0) {
        // If current start date is not available, set to the latest available date
        if (!dates.includes(startDate)) {
          setStartDate(dates[0]); // First date is the latest (sorted desc)
        }

        // If current end date is not available, set to the earliest available date that's <= current end date
        const validEndDates = dates.filter((date) => date <= endDate);
        if (validEndDates.length > 0 && !dates.includes(endDate)) {
          setEndDate(validEndDates[0]);
        } else if (!dates.includes(endDate)) {
          setEndDate(dates[dates.length - 1]); // Last date is the earliest (sorted desc)
        }
      }
    } catch (err) {
      console.warn('Failed to fetch available benchmark dates:', err);
    } finally {
      setLoadingAvailableDates(false);
    }
  };

  const fetchBenchmarks = async () => {
    // Disable benchmark computation if no region is selected
    if (!selectedRegion) {
      setBenchmarkData([]);
      setError(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const getToken = async () => {
        const { data } = await supabase.auth.getSession();
        const token = data.session?.access_token;
        if (!token) {
          throw new Error('No authentication token available');
        }
        return token;
      };

      const benchmarkService = new BenchmarkService(import.meta.env.VITE_API_BASE_URL, getToken);

      // Use selected dates
      const startTime = new Date(startDate + 'T00:00:00.000Z');
      const endTime = new Date(endDate + 'T23:59:59.999Z');

      // Fetch benchmarks for all selected models
      const modelResults = await Promise.all(
        Array.from(selectedModels).map(async (model) => {
          const query: BenchmarkQuery = {
            station_ids: stations.length > 0 ? stations.map((s) => s.station) : undefined,
            model,
            start_time: startTime.toISOString(),
            end_time: endTime.toISOString(),
          };

          const response = await benchmarkService.getStationMetrics(query);

          if (response.error) {
            console.warn(`Error fetching benchmarks for ${model}:`, response.error);
            return { model, data: [] };
          } else if (response.data) {
            return { model, data: response.data.data };
          }
          return { model, data: [] };
        }),
      );

      // Combine all model results
      const allBenchmarkData: StationMetrics[] = [];
      modelResults.forEach((result) => {
        if (result.data && result.data.length > 0) {
          // Add model info to each metric
          const dataWithModel = result.data.map((metric) => ({
            ...metric,
            model: result.model,
          }));
          allBenchmarkData.push(...dataWithModel);
        }
      });

      setBenchmarkData(allBenchmarkData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch benchmarks');
    } finally {
      setLoading(false);
    }
  };

  // Handle click outside for model dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;

      if (
        modelDropdownOpen &&
        modelDropdownRef.current &&
        !modelDropdownRef.current.contains(target)
      ) {
        setModelDropdownOpen(false);
      }
    };

    if (modelDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [modelDropdownOpen]);

  // Create chart when view mode changes to chart
  useEffect(() => {
    if (
      viewMode === 'chart' &&
      chartRef.current &&
      filteredBenchmarkData.length > 0 &&
      selectedRegion
    ) {
      // Dispose existing chart
      if (chartInstance.current) {
        chartInstance.current.dispose();
      }

      // Create new chart
      chartInstance.current = echarts.init(chartRef.current);

      const isDark = theme === 'dark';

      // Prepare data for selected variable, grouped by model
      const modelData = filteredBenchmarkData.reduce(
        (acc, metric) => {
          const variableMetrics = selectedVariable ? metric.metrics[selectedVariable] : null;
          const model = (metric as any).model || 'unknown';

          if (!acc[model]) {
            acc[model] = [];
          }

          if (variableMetrics?.rmse) {
            acc[model].push({
              predictionTime: metric.prediction_timedelta,
              rmse: variableMetrics.rmse,
            });
          }

          return acc;
        },
        {} as Record<string, Array<{ predictionTime: number; rmse: number }>>,
      );

      // Filter data to only include selected models
      const filteredModelData = Object.entries(modelData).filter(([model]) =>
        selectedModels.has(model),
      );

      if (filteredModelData.length === 0) return;

      // Calculate dynamic Y-axis range based on RMSE values only
      const allRmseValues = filteredModelData.flatMap(([, data]) =>
        data.map((d) => d.rmse).filter((v) => v > 0),
      );
      const minVal = Math.min(...allRmseValues);
      const maxVal = Math.max(...allRmseValues);
      const padding = (maxVal - minVal) * 0.1;
      const yMin = Math.max(0, minVal - padding);
      const yMax = maxVal + padding;

      // Model colors matching forecast page exactly
      const modelColorMap: Record<string, string> = {
        ept2: '#E34290', // JUA_BRAND_PINK
        ept2_early: '#B6F0FF', // JUA_BRAND_LIGHT_CYAN
        ept1_5: '#0AB8DC', // JUA_BRAND_CYAN
        ept2_e: '#FABD8F', // JUA_BRAND_ORANGE
        ept2_reasoning: '#2563EB', // Bright blue - matches colorUtils.ts
        ept2_hrrr: '#7C3AED', // Purple - HRRR variant (matches colorUtils.ts)
        aifs: '#EF4444', // JUA_COLOR_PALETTE[13] - Bright red
        aurora: '#10B981', // JUA_COLOR_PALETTE[14] - Emerald/Lime
        icon_global: '#7C3AED', // JUA_COLOR_PALETTE[5] - Purple - distinct from all others
        icon_eu: '#8B5CF6', // JUA_COLOR_PALETTE[15] - Violet (different from icon_global)
        ecmwf_ifs_single: '#F59E0B', // JUA_COLOR_PALETTE[12] - Amber
        ecmwf_ifs025_single: '#F59E0B', // JUA_COLOR_PALETTE[12] - Amber
        ecmwf_aifs025_single: '#7C3AED', // JUA_COLOR_PALETTE[5] - Purple
        gfs_graphcast025: '#6366F1', // JUA_COLOR_PALETTE[10] - Indigo
        noaa_gfs_single: '#EA580C', // Orange
      };

      const getModelColor = (modelId: string, fallbackIndex: number): string => {
        return (
          modelColorMap[modelId] ||
          ['#E34290', '#3B82F6', '#10B981', '#F59E0B', '#8B5CF6'][fallbackIndex % 5]
        );
      };

      const option: echarts.EChartsOption = {
        backgroundColor: 'transparent',
        grid: {
          left: '60px',
          right: '40px',
          top: '40px',
          bottom: '60px',
          containLabel: true,
        },
        legend: {
          show: filteredModelData.length > 1,
          top: '10px',
          left: 'center',
          textStyle: {
            color: isDark ? '#F9FAFB' : '#111827',
            fontSize: 12,
            fontWeight: 500,
          },
          itemGap: 20,
          selectedMode: true,
        },
        tooltip: {
          trigger: 'axis',
          backgroundColor: isDark ? 'rgba(17, 24, 39, 0.95)' : 'rgba(255, 255, 255, 0.95)',
          borderColor: isDark ? '#374151' : '#E5E7EB',
          borderWidth: 1,
          borderRadius: 8,
          padding: [12, 16],
          textStyle: {
            color: isDark ? '#F9FAFB' : '#111827',
            fontSize: 13,
          },
          formatter: (params: any) => {
            if (!params || params.length === 0) return '';

            const predTime = params[0].axisValue;
            const unit = selectedVariable ? variableConfig[selectedVariable]?.unit || '' : '';

            let content = `<div style="font-weight: 600; margin-bottom: 8px; color: ${isDark ? '#F3F4F6' : '#1F2937'};">Forecast Hours: ${predTime}</div>`;

            params.forEach((param: any) => {
              const color = param.color;
              const modelName = param.seriesName;
              const rmseValue = param.data[1];

              content += `<div style="display: flex; align-items: center; margin-bottom: 4px;">
                <span style="display: inline-block; width: 12px; height: 12px; border-radius: 50%; background: ${color}; margin-right: 12px;"></span>
                <span style="color: ${isDark ? '#E5E7EB' : '#374151'};">${modelName} RMSE: </span>
                <span style="font-weight: 600; margin-left: 4px; color: ${isDark ? '#F9FAFB' : '#111827'};">${rmseValue.toFixed(2)} ${unit}</span>
              </div>`;
            });

            return content;
          },
        },
        xAxis: {
          type: 'category',
          data: (() => {
            // Get all unique prediction times from all models
            const allTimes = new Set<number>();
            filteredModelData.forEach(([, data]) => {
              data.forEach((d) => allTimes.add(d.predictionTime));
            });

            return Array.from(allTimes)
              .sort((a, b) => a - b)
              .map((totalMinutes) => {
                // Special case: 0 minutes should be "0h" for immediate prediction
                if (totalMinutes === 0) return '0h';

                const totalHours = Math.floor(totalMinutes / 60);
                const days = Math.floor(totalHours / 24);
                const hours = totalHours % 24;
                const minutes = totalMinutes % 60;

                if (days === 0 && hours === 0) return `${minutes}m`;
                if (days === 0) return minutes === 0 ? `${hours}h` : `${hours}h ${minutes}m`;
                if (hours === 0 && minutes === 0) return `${days}d`;
                if (minutes === 0) return `${days}d ${hours}h`;
                return `${days}d ${hours}h ${minutes}m`;
              });
          })(),
          axisLine: {
            lineStyle: { color: isDark ? '#4B5563' : '#D1D5DB', width: 1 },
          },
          axisLabel: {
            color: isDark ? '#9CA3AF' : '#6B7280',
            fontSize: 12,
            fontWeight: 500,
            formatter: (value: string, index: number) => {
              // Get all unique prediction times from all models
              const allTimes = new Set<number>();
              filteredModelData.forEach(([, data]) => {
                data.forEach((d) => allTimes.add(d.predictionTime));
              });
              const sortedTimes = Array.from(allTimes).sort((a, b) => a - b);
              const totalMinutes = sortedTimes[index];

              if (totalMinutes === undefined) return value;

              const totalHours = Math.floor(totalMinutes / 60);
              const days = Math.floor(totalHours / 24);
              const hours = totalHours % 24;

              // Determine what constitutes a "nice" label based on data range
              const maxMinutes = Math.max(...sortedTimes);
              const maxDays = Math.floor(maxMinutes / 60 / 24);

              // For forecasts <= 2 days: show 0h, 6h, 12h, 18h, 1d, 1d 12h, 2d
              // For forecasts <= 5 days: show 0h, 12h, 1d, 2d, 3d, 4d, 5d
              // For forecasts > 5 days: show 0h, 1d, 2d, 3d, 4d, 5d, 6d, 7d, etc.

              if (maxDays <= 2) {
                // Show 6-hour intervals at nice times
                if (
                  totalMinutes === 0 ||
                  (days === 0 && hours % 6 === 0) ||
                  (days > 0 && (hours === 0 || hours === 12))
                ) {
                  return value;
                }
              } else if (maxDays <= 5) {
                // Show 12-hour intervals at nice times
                if (
                  totalMinutes === 0 ||
                  (days === 0 && hours % 12 === 0) ||
                  (days > 0 && hours === 0)
                ) {
                  return value;
                }
              } else {
                // Show daily intervals only
                if (totalMinutes === 0 || hours === 0) {
                  return value;
                }
              }

              // Return empty string for non-nice labels (ECharts will hide them)
              return '';
            },
            showMaxLabel: true, // Always show the last label
            showMinLabel: true, // Always show the first label
          },
          splitLine: { show: false },
        },
        yAxis: {
          type: 'value',
          name: `RMSE (${selectedVariable ? variableConfig[selectedVariable]?.unit || '' : ''})`,
          nameLocation: 'middle',
          nameGap: 45,
          nameRotate: 90,
          min: yMin,
          max: yMax,
          nameTextStyle: {
            color: isDark ? '#9CA3AF' : '#6B7280',
            fontSize: 12,
            fontWeight: 500,
          },
          axisLine: {
            lineStyle: { color: isDark ? '#4B5563' : '#D1D5DB', width: 1 },
          },
          axisLabel: {
            color: isDark ? '#9CA3AF' : '#6B7280',
            fontSize: 12,
            fontWeight: 500,
            formatter: (value: number) => value.toFixed(2),
          },
          splitLine: {
            lineStyle: {
              color: isDark ? '#374151' : '#F3F4F6',
              type: 'solid',
              width: 1,
            },
          },
        },
        series: filteredModelData.map(([model, data], index) => {
          const modelLabel = MODELS.find((m) => m.id === model)?.label || model.toUpperCase();
          const color = getModelColor(model, index);

          // Sort data by prediction time and create time-indexed map
          const sortedData = data.sort((a, b) => a.predictionTime - b.predictionTime);
          const timeValueMap = new Map(sortedData.map((d) => [d.predictionTime, d.rmse]));

          // Get all unique times and map values
          const allTimes = new Set<number>();
          filteredModelData.forEach(([, modelData]) => {
            modelData.forEach((d) => allTimes.add(d.predictionTime));
          });

          const seriesData = Array.from(allTimes)
            .sort((a, b) => a - b)
            .map((time, timeIndex) => {
              const rmse = timeValueMap.get(time);
              return rmse !== undefined ? [timeIndex, rmse] : null;
            })
            .filter((item) => item !== null);

          return {
            name: modelLabel,
            type: 'line',
            data: seriesData,
            lineStyle: {
              color: color,
              width: 2,
            },
            itemStyle: { color: color },
            symbol: 'circle',
            symbolSize: 4,
            smooth: true,
            emphasis: {
              focus: 'series',
              lineStyle: { width: 2 },
            },
            connectNulls: false,
          };
        }),
      };

      chartInstance.current.setOption(option);

      // Handle resize
      const handleResize = () => {
        if (chartInstance.current) {
          chartInstance.current.resize();
        }
      };

      window.addEventListener('resize', handleResize);

      return () => {
        window.removeEventListener('resize', handleResize);
      };
    } else if (viewMode === 'table' && chartInstance.current) {
      // Dispose chart when switching to table view
      chartInstance.current.dispose();
      chartInstance.current = null;
    }
  }, [viewMode, filteredBenchmarkData, theme, selectedVariable, selectedModels, selectedRegion]);

  // Cleanup chart on unmount
  useEffect(() => {
    return () => {
      if (chartInstance.current) {
        chartInstance.current.dispose();
      }
    };
  }, []);

  if (!isOpen) return null;

  return (
    <>
      <style>
        {`
          .horizon-slider::-webkit-slider-thumb {
            -webkit-appearance: none;
            appearance: none;
            width: 18px;
            height: 18px;
            background: #E34290;
            border-radius: 50%;
            cursor: pointer;
            box-shadow: 0 2px 6px rgba(227, 66, 144, 0.4);
            transition: transform 0.15s ease, box-shadow 0.15s ease;
          }
          .horizon-slider::-webkit-slider-thumb:hover {
            transform: scale(1.15);
            box-shadow: 0 3px 10px rgba(227, 66, 144, 0.5);
          }
          .horizon-slider::-moz-range-thumb {
            width: 18px;
            height: 18px;
            background: #E34290;
            border: none;
            border-radius: 50%;
            cursor: pointer;
            box-shadow: 0 2px 6px rgba(227, 66, 144, 0.4);
          }
          .horizon-slider::-moz-range-thumb:hover {
            transform: scale(1.15);
          }
        `}
      </style>
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          zIndex: 1000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px',
          overflow: 'hidden',
        }}
        onClick={onClose}
      >
        <div
          style={{
            backgroundColor: theme === 'dark' ? '#1F2937' : '#FFFFFF',
            borderRadius: '12px',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
            width: '1200px',
            height: '80vh',
            minHeight: '600px',
            maxHeight: '800px',
            display: 'flex',
            flexDirection: 'column',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div
            style={{
              padding: '16px 20px',
              borderBottom: `1px solid ${theme === 'dark' ? '#374151' : '#E5E7EB'}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <div>
              <h2
                style={{
                  margin: 0,
                  fontSize: '18px',
                  fontWeight: '600',
                  color: theme === 'dark' ? '#F9FAFB' : '#1F2937',
                  marginBottom: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}
              >
                <BarChart3 size={20} />
                Station Benchmarks
              </h2>
              <p
                style={{
                  margin: 0,
                  fontSize: '14px',
                  color: theme === 'dark' ? '#9CA3AF' : '#6B7280',
                }}
              >
                RMSE metrics for {stations.length} stations from{' '}
                {new Date(startDate).toLocaleDateString()} to{' '}
                {new Date(endDate).toLocaleDateString()}{' '}
                {selectedRegion ? 'in selected region' : ''}
                {loadingAvailableDates && (
                  <span style={{ marginLeft: '8px', fontStyle: 'italic' }}>
                    (Loading available dates...)
                  </span>
                )}
                {availableDates.length > 0 && !loadingAvailableDates && (
                  <span style={{ marginLeft: '8px', fontSize: '12px', opacity: 0.8 }}>
                    • {availableDates.length} benchmark dates available
                    {(() => {
                      // Show date range for available dates
                      if (availableDates.length > 0) {
                        const latest = availableDates[0]; // sorted desc
                        const earliest = availableDates[availableDates.length - 1];
                        const latestDate = new Date(latest).toLocaleDateString();
                        const earliestDate = new Date(earliest).toLocaleDateString();
                        return ` (${earliestDate} to ${latestDate})`;
                      }
                      return '';
                    })()}
                  </span>
                )}
              </p>
            </div>
            <button
              onClick={onClose}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '4px',
                borderRadius: '4px',
                color: theme === 'dark' ? '#9CA3AF' : '#6B7280',
              }}
            >
              <X size={20} />
            </button>
          </div>

          {/* Controls */}
          <div
            style={{
              padding: '16px 20px',
              borderBottom: `1px solid ${theme === 'dark' ? '#374151' : '#E5E7EB'}`,
            }}
          >
            <div
              style={{
                display: 'flex',
                gap: '20px',
                alignItems: 'end',
                width: '100%',
                flexWrap: 'wrap',
              }}
            >
              <div style={{ flex: '1', minWidth: '220px', maxWidth: '220px' }}>
                <label
                  style={{
                    fontSize: '11px',
                    fontWeight: '600',
                    fontFamily: 'system-ui, -apple-system, sans-serif',
                    color: theme === 'dark' ? '#F3F4F6' : '#374151',
                    marginBottom: '6px',
                    display: 'block',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                  }}
                >
                  🤖 Models
                </label>
                <div style={{ position: 'relative' }} ref={modelDropdownRef}>
                  <button
                    onClick={() => setModelDropdownOpen(!modelDropdownOpen)}
                    style={{
                      padding: '6px 10px',
                      paddingRight: '30px',
                      backgroundColor: theme === 'dark' ? '#1F2937' : '#FFFFFF',
                      border: `2px solid ${theme === 'dark' ? '#4B5563' : '#E5E7EB'}`,
                      borderRadius: '6px',
                      fontSize: '12px',
                      fontWeight: '500',
                      fontFamily: 'system-ui, -apple-system, sans-serif',
                      color: theme === 'dark' ? '#F9FAFB' : '#1F2937',
                      outline: 'none',
                      width: '100%',
                      minWidth: '200px',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      boxShadow:
                        theme === 'dark'
                          ? '0 1px 3px rgba(0, 0, 0, 0.3)'
                          : '0 1px 3px rgba(0, 0, 0, 0.1)',
                      appearance: 'none',
                      backgroundImage: `url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='${theme === 'dark' ? '%23a1a1aa' : '%236b7280'}' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6,9 12,15 18,9'%3e%3c/polyline%3e%3c/svg%3e")`,
                      backgroundRepeat: 'no-repeat',
                      backgroundPosition: 'right 8px center',
                      backgroundSize: '16px',
                      textAlign: 'left',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'flex-start',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                    onFocus={(e) => {
                      (e.target as HTMLButtonElement).style.borderColor = '#3B82F6';
                      (e.target as HTMLButtonElement).style.boxShadow =
                        theme === 'dark'
                          ? '0 0 0 3px rgba(59, 130, 246, 0.1), 0 1px 3px rgba(0, 0, 0, 0.3)'
                          : '0 0 0 3px rgba(59, 130, 246, 0.1), 0 1px 3px rgba(0, 0, 0, 0.1)';
                    }}
                    onBlur={(e) => {
                      (e.target as HTMLButtonElement).style.borderColor =
                        theme === 'dark' ? '#4B5563' : '#E5E7EB';
                      (e.target as HTMLButtonElement).style.boxShadow =
                        theme === 'dark'
                          ? '0 1px 3px rgba(0, 0, 0, 0.3)'
                          : '0 1px 3px rgba(0, 0, 0, 0.1)';
                    }}
                    onMouseOver={(e) => {
                      (e.target as HTMLButtonElement).style.borderColor =
                        theme === 'dark' ? '#6B7280' : '#D1D5DB';
                    }}
                    onMouseOut={(e) => {
                      if (document.activeElement !== e.target) {
                        (e.target as HTMLButtonElement).style.borderColor =
                          theme === 'dark' ? '#4B5563' : '#E5E7EB';
                      }
                    }}
                  >
                    <span>
                      {selectedModels.size === 0
                        ? 'Select models...'
                        : selectedModels.size === 1
                          ? MODELS.find((m) => m.id === Array.from(selectedModels)[0])?.label ||
                            Array.from(selectedModels)[0]
                          : `${selectedModels.size} models selected`}
                    </span>
                  </button>

                  {modelDropdownOpen && (
                    <div
                      style={{
                        position: 'absolute',
                        top: '100%',
                        left: '0',
                        right: '0',
                        backgroundColor: theme === 'dark' ? '#1F2937' : '#FFFFFF',
                        border: `1px solid ${theme === 'dark' ? '#4B5563' : '#E5E7EB'}`,
                        borderRadius: '6px',
                        marginTop: '4px',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                        zIndex: 50,
                        maxHeight: '200px',
                        overflowY: 'auto',
                      }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {MODELS.map((model) => {
                        const isSelected = selectedModels.has(model.id);
                        return (
                          <div
                            key={model.id}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();

                              const newSelected = new Set(selectedModels);
                              if (isSelected) {
                                newSelected.delete(model.id);
                              } else {
                                newSelected.add(model.id);
                              }
                              setSelectedModels(newSelected);
                            }}
                            style={{
                              width: '100%',
                              textAlign: 'left',
                              padding: '8px 12px',
                              cursor: 'pointer',
                              fontSize: '12px',
                              fontWeight: isSelected ? '500' : '400',
                              backgroundColor: isSelected
                                ? theme === 'dark'
                                  ? 'rgba(236, 72, 153, 0.2)'
                                  : '#FDF2F8' // bg-pink-50 dark:bg-pink-900/20
                                : 'transparent',
                              color: isSelected
                                ? theme === 'dark'
                                  ? '#F472B6'
                                  : '#DB2777' // text-pink-600 dark:text-pink-400
                                : theme === 'dark'
                                  ? '#9CA3AF'
                                  : '#6B7280', // text-gray-600 dark:text-gray-300
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              transition: 'all 0.2s ease',
                              border: 'none',
                            }}
                            onMouseOver={(e) => {
                              if (!isSelected) {
                                (e.target as HTMLDivElement).style.backgroundColor =
                                  theme === 'dark' ? '#374151' : '#F9FAFB'; // hover:bg-gray-50 dark:hover:bg-gray-700
                              }
                            }}
                            onMouseOut={(e) => {
                              if (!isSelected) {
                                (e.target as HTMLDivElement).style.backgroundColor = 'transparent';
                              } else {
                                (e.target as HTMLDivElement).style.backgroundColor =
                                  theme === 'dark' ? 'rgba(236, 72, 153, 0.2)' : '#FDF2F8';
                              }
                            }}
                          >
                            <span style={{ textOverflow: 'ellipsis', overflow: 'hidden' }}>
                              {model.label}
                            </span>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                              {isSelected && (
                                <div
                                  style={{
                                    width: '8px', // w-2
                                    height: '8px', // h-2
                                    borderRadius: '50%',
                                    marginLeft: '4px', // ml-1
                                    backgroundColor: '#EC4899', // bg-pink-500
                                    flexShrink: 0,
                                  }}
                                />
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>

              {/* Variable Selection */}
              <div style={{ flex: '1', minWidth: '200px' }}>
                <label
                  style={{
                    fontSize: '11px',
                    fontWeight: '600',
                    fontFamily: 'system-ui, -apple-system, sans-serif',
                    color: theme === 'dark' ? '#F3F4F6' : '#374151',
                    marginBottom: '6px',
                    display: 'block',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                  }}
                >
                  <BarChart3 size={16} className="inline mr-2" />
                  Variable
                </label>
                <select
                  value={selectedVariable}
                  onChange={(e) => setSelectedVariable(e.target.value)}
                  style={{
                    padding: '6px 10px',
                    paddingRight: '30px', // Space for arrow, moved closer
                    backgroundColor: theme === 'dark' ? '#1F2937' : '#FFFFFF',
                    border: `2px solid ${theme === 'dark' ? '#4B5563' : '#E5E7EB'}`,
                    borderRadius: '6px',
                    fontSize: '12px',
                    fontWeight: '500',
                    fontFamily: 'system-ui, -apple-system, sans-serif', // Uniform font
                    color: theme === 'dark' ? '#F9FAFB' : '#1F2937',
                    outline: 'none',
                    width: '100%',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    boxShadow:
                      theme === 'dark'
                        ? '0 1px 3px rgba(0, 0, 0, 0.3)'
                        : '0 1px 3px rgba(0, 0, 0, 0.1)',
                    appearance: 'none',
                    backgroundImage: `url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='${theme === 'dark' ? '%23a1a1aa' : '%236b7280'}' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6,9 12,15 18,9'%3e%3c/polyline%3e%3c/svg%3e")`,
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'right 8px center', // Moved arrow closer to left
                    backgroundSize: '16px',
                  }}
                  onFocus={(e) => {
                    (e.target as HTMLSelectElement).style.borderColor = '#3B82F6';
                    (e.target as HTMLSelectElement).style.boxShadow =
                      theme === 'dark'
                        ? '0 0 0 3px rgba(59, 130, 246, 0.1), 0 1px 3px rgba(0, 0, 0, 0.3)'
                        : '0 0 0 3px rgba(59, 130, 246, 0.1), 0 1px 3px rgba(0, 0, 0, 0.1)';
                  }}
                  onBlur={(e) => {
                    (e.target as HTMLSelectElement).style.borderColor =
                      theme === 'dark' ? '#4B5563' : '#E5E7EB';
                    (e.target as HTMLSelectElement).style.boxShadow =
                      theme === 'dark'
                        ? '0 1px 3px rgba(0, 0, 0, 0.3)'
                        : '0 1px 3px rgba(0, 0, 0, 0.1)';
                  }}
                  onMouseOver={(e) => {
                    (e.target as HTMLSelectElement).style.borderColor =
                      theme === 'dark' ? '#6B7280' : '#D1D5DB';
                  }}
                  onMouseOut={(e) => {
                    if (document.activeElement !== e.target) {
                      (e.target as HTMLSelectElement).style.borderColor =
                        theme === 'dark' ? '#4B5563' : '#E5E7EB';
                    }
                  }}
                >
                  {/* Show variables from data if available, otherwise show default variables */}
                  {benchmarkData.length > 0
                    ? Object.keys(benchmarkData[0]?.metrics || {}).map((variable) => (
                        <option key={variable} value={variable}>
                          {variableConfig[variable]?.name || variable}
                        </option>
                      ))
                    : Object.keys(variableConfig).map((variable) => (
                        <option key={variable} value={variable}>
                          {variableConfig[variable]?.name || variable}
                        </option>
                      ))}
                </select>
              </div>

              {/* Start Date */}
              <div style={{ flex: '1', minWidth: '150px' }}>
                <label
                  style={{
                    fontSize: '11px',
                    fontWeight: '600',
                    fontFamily: 'system-ui, -apple-system, sans-serif',
                    color: theme === 'dark' ? '#F3F4F6' : '#374151',
                    marginBottom: '6px',
                    display: 'block',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                  }}
                >
                  Start Date
                </label>
                <CustomDatePicker
                  value={startDate}
                  onChange={handleStartDateChange}
                  placeholder="Select start date"
                  maxDate={endDate} // Don't allow start date to be after end date
                  availableDates={availableDates.length > 0 ? availableDates : undefined}
                />
              </div>

              {/* End Date */}
              <div style={{ flex: '1', minWidth: '150px' }}>
                <label
                  style={{
                    fontSize: '11px',
                    fontWeight: '600',
                    fontFamily: 'system-ui, -apple-system, sans-serif',
                    color: theme === 'dark' ? '#F3F4F6' : '#374151',
                    marginBottom: '6px',
                    display: 'block',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                  }}
                >
                  End Date
                </label>
                <CustomDatePicker
                  value={endDate}
                  onChange={handleEndDateChange}
                  placeholder="Select end date"
                  minDate={startDate} // Disable dates before start date
                  maxDate={latestObservationDate} // Disable dates after latest observation
                  availableDates={availableDates.length > 0 ? availableDates : undefined}
                />
              </div>

              {/* Forecast Horizon Slider */}
              <div style={{ flex: '0 0 auto', minWidth: '140px' }}>
                <label
                  style={{
                    fontSize: '11px',
                    fontWeight: '600',
                    fontFamily: 'system-ui, -apple-system, sans-serif',
                    color: theme === 'dark' ? '#F3F4F6' : '#374151',
                    marginBottom: '6px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                  }}
                >
                  Horizon
                  <span
                    style={{
                      backgroundColor: '#E34290',
                      color: '#FFFFFF',
                      padding: '2px 6px',
                      borderRadius: '10px',
                      fontSize: '10px',
                      fontWeight: '700',
                      textTransform: 'none',
                      letterSpacing: '0',
                      minWidth: '28px',
                      textAlign: 'center',
                    }}
                  >
                    {maxForecastHours < 24
                      ? `${maxForecastHours}h`
                      : `${Math.round(maxForecastHours / 24)}d`}
                  </span>
                </label>
                <div
                  style={{
                    height: '32px',
                    display: 'flex',
                    alignItems: 'center',
                    padding: '0 10px',
                    backgroundColor: theme === 'dark' ? '#1F2937' : '#FFFFFF',
                    border: `2px solid ${theme === 'dark' ? '#4B5563' : '#E5E7EB'}`,
                    borderRadius: '6px',
                    boxShadow:
                      theme === 'dark'
                        ? '0 1px 3px rgba(0, 0, 0, 0.3)'
                        : '0 1px 3px rgba(0, 0, 0, 0.1)',
                  }}
                >
                  <input
                    type="range"
                    min="12"
                    max="168"
                    step="12"
                    value={maxForecastHours}
                    onChange={(e) => setMaxForecastHours(Number(e.target.value))}
                    className="horizon-slider"
                    style={{
                      width: '100%',
                      height: '6px',
                      WebkitAppearance: 'none',
                      appearance: 'none',
                      background: `linear-gradient(to right, #E34290 0%, #E34290 ${((maxForecastHours - 12) / (168 - 12)) * 100}%, ${theme === 'dark' ? '#4B5563' : '#D1D5DB'} ${((maxForecastHours - 12) / (168 - 12)) * 100}%, ${theme === 'dark' ? '#4B5563' : '#D1D5DB'} 100%)`,
                      borderRadius: '3px',
                      cursor: 'pointer',
                      outline: 'none',
                    }}
                  />
                </div>
              </div>

              {/* View Mode Toggle */}
              <div style={{ flex: '0 0 auto', minWidth: '120px' }}>
                <label
                  style={{
                    fontSize: '11px',
                    fontWeight: '600',
                    fontFamily: 'system-ui, -apple-system, sans-serif',
                    color: theme === 'dark' ? '#F3F4F6' : '#374151',
                    marginBottom: '6px',
                    display: 'block',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                  }}
                >
                  View
                </label>
                <div
                  style={{
                    display: 'flex',
                    border: `2px solid ${theme === 'dark' ? '#4B5563' : '#E5E7EB'}`,
                    borderRadius: '6px',
                    overflow: 'hidden',
                    transition: 'all 0.2s ease',
                    boxShadow:
                      theme === 'dark'
                        ? '0 1px 3px rgba(0, 0, 0, 0.3)'
                        : '0 1px 3px rgba(0, 0, 0, 0.1)',
                  }}
                >
                  <button
                    onClick={() => setViewMode('table')}
                    disabled={!selectedRegion}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '3px',
                      padding: '6px 10px',
                      backgroundColor:
                        viewMode === 'table' ? '#E34290' : theme === 'dark' ? '#1F2937' : '#FFFFFF',
                      color:
                        viewMode === 'table' ? '#FFFFFF' : theme === 'dark' ? '#F9FAFB' : '#1F2937',
                      border: 'none',
                      fontSize: '12px',
                      fontWeight: '500',
                      fontFamily: 'system-ui, -apple-system, sans-serif',
                      cursor: selectedRegion ? 'pointer' : 'not-allowed',
                      transition: 'all 0.2s ease',
                      flex: '1',
                      opacity: selectedRegion ? 1 : 0.5,
                    }}
                    onMouseOver={(e) => {
                      if (viewMode !== 'table' && selectedRegion) {
                        (e.target as HTMLButtonElement).style.backgroundColor =
                          theme === 'dark' ? '#374151' : '#F3F4F6';
                      }
                    }}
                    onMouseOut={(e) => {
                      if (viewMode !== 'table') {
                        (e.target as HTMLButtonElement).style.backgroundColor =
                          theme === 'dark' ? '#1F2937' : '#FFFFFF';
                      }
                    }}
                  >
                    <Table size={11} />
                    Table
                  </button>
                  <button
                    onClick={() => setViewMode('chart')}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '3px',
                      padding: '6px 10px',
                      backgroundColor:
                        viewMode === 'chart' ? '#E34290' : theme === 'dark' ? '#1F2937' : '#FFFFFF',
                      color:
                        viewMode === 'chart' ? '#FFFFFF' : theme === 'dark' ? '#F9FAFB' : '#1F2937',
                      border: 'none',
                      fontSize: '12px',
                      fontWeight: '500',
                      fontFamily: 'system-ui, -apple-system, sans-serif',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      flex: '1',
                      borderLeft: `1px solid ${theme === 'dark' ? '#4B5563' : '#E5E7EB'}`,
                    }}
                    onMouseOver={(e) => {
                      if (viewMode !== 'chart') {
                        (e.target as HTMLButtonElement).style.backgroundColor =
                          theme === 'dark' ? '#374151' : '#F3F4F6';
                      }
                    }}
                    onMouseOut={(e) => {
                      if (viewMode !== 'chart') {
                        (e.target as HTMLButtonElement).style.backgroundColor =
                          theme === 'dark' ? '#1F2937' : '#FFFFFF';
                      }
                    }}
                  >
                    <LineChart size={11} />
                    Chart
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Content */}
          <div style={{ flex: 1, overflow: 'auto', padding: '20px' }}>
            {loading && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '200px',
                  flexDirection: 'column',
                  gap: '12px',
                }}
              >
                <Loader2 size={32} className="animate-spin" style={{ color: '#E34290' }} />
                <p style={{ color: theme === 'dark' ? '#9CA3AF' : '#6B7280', fontSize: '14px' }}>
                  Loading benchmarks...
                </p>
              </div>
            )}

            {error && (
              <div
                style={{
                  padding: '16px',
                  backgroundColor: theme === 'dark' ? '#7F1D1D' : '#FEE2E2',
                  border: `1px solid ${theme === 'dark' ? '#B91C1C' : '#FECACA'}`,
                  borderRadius: '6px',
                  color: theme === 'dark' ? '#FCA5A5' : '#B91C1C',
                  fontSize: '14px',
                }}
              >
                <strong>Error:</strong> {error}
              </div>
            )}

            {!loading && !error && filteredBenchmarkData.length === 0 && (
              <div
                style={{
                  textAlign: 'center',
                  padding: '40px 20px',
                  color: theme === 'dark' ? '#9CA3AF' : '#6B7280',
                }}
              >
                <Target size={48} style={{ margin: '0 auto 16px', opacity: 0.5 }} />
                <p style={{ margin: 0, fontSize: '16px', fontWeight: '500' }}>
                  No benchmark data available
                </p>
                <p style={{ margin: '4px 0 0', fontSize: '14px' }}>
                  Try selecting different stations or time range
                </p>
              </div>
            )}

            {!loading && !error && filteredBenchmarkData.length > 0 && (
              <div>
                <h3
                  style={{
                    margin: '0 0 16px',
                    fontSize: '16px',
                    fontWeight: '600',
                    color: theme === 'dark' ? '#F9FAFB' : '#1F2937',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                  }}
                >
                  {viewMode === 'table' ? <Table size={16} /> : <LineChart size={16} />}
                  Error Metrics by Forecast Hours
                </h3>

                {viewMode === 'table' ? (
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr>
                          <th
                            style={{
                              padding: '6px 8px',
                              textAlign: 'left',
                              fontSize: '12px',
                              fontWeight: '600',
                              color: theme === 'dark' ? '#D1D5DB' : '#6B7280',
                              borderBottom: `1px solid ${theme === 'dark' ? '#374151' : '#E5E7EB'}`,
                            }}
                          >
                            Forecast Hours
                          </th>
                          {Array.from(selectedModels).map((model) => (
                            <th
                              key={model}
                              style={{
                                padding: '6px 8px',
                                textAlign: 'center',
                                fontSize: '12px',
                                fontWeight: '600',
                                color: theme === 'dark' ? '#D1D5DB' : '#6B7280',
                                borderBottom: `1px solid ${theme === 'dark' ? '#374151' : '#E5E7EB'}`,
                              }}
                            >
                              {MODELS.find((m) => m.id === model)?.label || model.toUpperCase()}{' '}
                              RMSE
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {(() => {
                          // Group data by prediction time
                          const timeGroups: Record<number, Record<string, number>> = {};

                          filteredBenchmarkData.forEach(
                            (metric: StationMetrics & { model?: string }) => {
                              const variableMetrics = selectedVariable
                                ? metric.metrics[selectedVariable]
                                : null;
                              const model = metric.model || 'unknown';

                              if (variableMetrics?.rmse && selectedModels.has(model)) {
                                const predTime = metric.prediction_timedelta;
                                if (!timeGroups[predTime]) {
                                  timeGroups[predTime] = {};
                                }
                                timeGroups[predTime][model] = variableMetrics.rmse;
                              }
                            },
                          );

                          return Object.entries(timeGroups)
                            .sort(([a], [b]) => Number(a) - Number(b))
                            .map(([predTimeStr, modelData]) => {
                              const predTime = Number(predTimeStr);
                              return (
                                <tr key={predTime}>
                                  <td
                                    style={{
                                      padding: '6px 8px',
                                      fontSize: '14px',
                                      fontWeight: '500',
                                      color: theme === 'dark' ? '#F9FAFB' : '#1F2937',
                                      borderBottom: `1px solid ${theme === 'dark' ? '#374151' : '#E5E7EB'}`,
                                    }}
                                  >
                                    {(() => {
                                      const totalMinutes = predTime;

                                      // Special case: 0 minutes should be "0h" for immediate prediction
                                      if (totalMinutes === 0) return '0h';

                                      const totalHours = Math.floor(totalMinutes / 60);
                                      const days = Math.floor(totalHours / 24);
                                      const hours = totalHours % 24;
                                      const minutes = totalMinutes % 60;

                                      if (days === 0 && hours === 0) return `${minutes}m`;
                                      if (days === 0)
                                        return minutes === 0
                                          ? `${hours}h`
                                          : `${hours}h ${minutes}m`;
                                      if (hours === 0 && minutes === 0) return `${days}d`;
                                      if (minutes === 0) return `${days}d ${hours}h`;
                                      return `${days}d ${hours}h ${minutes}m`;
                                    })()}
                                  </td>
                                  {Array.from(selectedModels).map((model) => (
                                    <td
                                      key={model}
                                      style={{
                                        padding: '6px 8px',
                                        textAlign: 'center',
                                        fontSize: '12px',
                                        fontFamily: 'monospace',
                                        color: theme === 'dark' ? '#9CA3AF' : '#6B7280',
                                        borderBottom: `1px solid ${theme === 'dark' ? '#374151' : '#E5E7EB'}`,
                                      }}
                                    >
                                      {modelData[model] !== undefined
                                        ? modelData[model].toFixed(3)
                                        : 'N/A'}
                                    </td>
                                  ))}
                                </tr>
                              );
                            });
                        })()}
                      </tbody>
                    </table>
                  </div>
                ) : viewMode === 'chart' ? (
                  <div
                    key="chart-container"
                    ref={chartRef}
                    style={{
                      width: '100%',
                      height: '400px',
                      borderRadius: '12px',
                    }}
                    className="chart-container bg-gray-50/50 dark:bg-gray-800/30 rounded-xl"
                  />
                ) : (
                  <div
                    style={{
                      textAlign: 'center',
                      padding: '40px 20px',
                      color: theme === 'dark' ? '#9CA3AF' : '#6B7280',
                    }}
                  >
                    <p style={{ margin: 0, fontSize: '16px', fontWeight: '500' }}>
                      No data available for table view
                    </p>
                    <p style={{ margin: '4px 0 0', fontSize: '14px' }}>
                      Try switching to chart view or adjusting your filters
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default BenchmarkModal;
